/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/tr";

// Bootstrap — alttaki HomeAnnouncement Swiper ile aynı genişlikte hizalamak için.
import Container from "react-bootstrap/Container";
import {
  FaRegCalendarAlt,
  FaCheckCircle,
  FaCrown,
  FaAward,
  FaBell,
  FaExclamationTriangle,
  FaRegClock,
} from "react-icons/fa";

import { API } from "@services/http.service";

import "./styles.scss";

moment.locale("tr");

/**
 * Admin "Eşik Ayarları" verisi gelmezse (eski backend) kullanılan yedek eşikler.
 * Connection default paleti + placeholder tabanlı mesajlar (admin seed'iyle aynı).
 */
const DEFAULT_THRESHOLDS = [
  {
    thresholdPercentage: 75,
    color: "#F59E0B",
    locked: false,
    messageText:
      "Bu ayki kampanya bütçemizin [YUZDE]'i kullanıldı. Alımlarınızı hızlandırın!",
  },
  {
    thresholdPercentage: 90,
    color: "#EA580C",
    locked: false,
    messageText:
      "Dikkat! Bu ayki kampanya bütçesinin [YUZDE]'i doldu. Bütçe bitmeden alımlarınızı yapın.",
  },
  {
    thresholdPercentage: 100,
    color: "#6B7280",
    locked: true,
    messageText:
      "Bu [AY_ADI] kampanya bütçesi tamamlandı. Bu ay için puan kazanımı sona erdi. [SONRAKI_AY] başında bütçemiz yenilenecek.",
  },
];

/**
 * Ana sayfa kartı: aylık kampanya bütçesi doluluk durumu (Story 2 + Yorum 10).
 * Program bitiş sayacı (Story 1) üst header'daki takvim widget'ında gösterilir.
 *
 * Sadece İşletme / Usta segmentindeki kullanıcılara gösterilir (gating Home'da).
 */
const HomeBudgetStrip = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    API.get("/management/budget/me/strip")
      .then((res) => {
        if (active && res?.isHttpSuccess) {
          setData(res.data);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  if (!data || !data.programDateEnd) {
    return null;
  }

  const now = moment();
  const pct = Math.min(Math.max(data.percentage || 0, 0), 100);
  const nextMonthName = moment().add(1, "month").startOf("month").format("MMMM");

  // Eşik metnindeki placeholder'ları çözer (genel havuz — kişisel veri yok).
  const resolveMsg = (text, thrPct) =>
    (text || "")
      .replace(/\[YUZDE\]/g, `%${thrPct}`)
      .replace(/\[AY_ADI\]/g, now.format("MMMM"))
      .replace(/\[SONRAKI_AY\]/g, nextMonthName);

  // Admin "Eşik Ayarları" (yoksa yedek). Aktif olanlar, yüzdeye göre artan sıra.
  const thresholds = (
    data.thresholds && data.thresholds.length ? data.thresholds : DEFAULT_THRESHOLDS
  )
    .filter((t) => t.enabled !== false)
    .slice()
    .sort((a, b) => a.thresholdPercentage - b.thresholdPercentage);

  // Zorunlu (%100) eşik → "puan kazanımı durdu" sınırı.
  const lockedT =
    thresholds.find((t) => t.locked) ||
    thresholds.find((t) => t.thresholdPercentage >= 100) ||
    thresholds[thresholds.length - 1];
  const stopAt = lockedT ? lockedT.thresholdPercentage : 100;
  const isComplete = pct >= stopAt;

  // Aşılan en yüksek eşik (renk + mesaj + rozet bunun üzerinden).
  const crossed = [...thresholds].reverse().find((t) => pct >= t.thresholdPercentage) || null;
  const nonLocked = thresholds.filter(
    (t) => !(t.locked || t.thresholdPercentage >= 100)
  );
  const isTopWarning =
    crossed &&
    nonLocked.length > 0 &&
    crossed.thresholdPercentage ===
      nonLocked[nonLocked.length - 1].thresholdPercentage;

  let badge = null;
  if (isComplete) {
    badge = "Tamamlandı";
  } else if (crossed) {
    badge = isTopWarning ? "Hedefe Yakın" : "İyi Gidiyor";
  }

  // Renk: tamamlandıysa locked rengi, değilse aşılan eşik rengi; aşılmadıysa yeşil.
  const accentColor = isComplete
    ? lockedT?.color || "#6B7280"
    : crossed?.color || null;
  const accentStyle = accentColor
    ? { "--hb-accent": accentColor, "--hb-accent-soft": `${accentColor}1A` }
    : undefined;

  // İlerleme işaretleri: 0 + eşik yüzdeleri (admin tanımına göre).
  const marks = Array.from(
    new Set([0, ...thresholds.map((t) => t.thresholdPercentage)])
  ).sort((a, b) => a - b);

  const alertText =
    !isComplete && crossed
      ? resolveMsg(crossed.messageText, crossed.thresholdPercentage)
      : null;
  const completedText = lockedT?.messageText
    ? resolveMsg(lockedT.messageText, stopAt)
    : `Yeni dönem başında bütçe yenilenecek.`;

  return (
    <Container className="home-budget">
      <div className="hb-card" style={accentStyle}>
        {/* Üst bilgi satırı */}
        <div className="hb-top">
          <div className="hb-seg hb-seg-title">
            <div className="hb-cal">
              <FaRegCalendarAlt />
            </div>
            <div>
              <div className="hb-month">{now.format("MMMM")}</div>
              <div className="hb-sub">İşletme Kampanya Bütçesi</div>
            </div>
          </div>

          <span className="hb-vline" />

          <div className="hb-seg hb-seg-stat">
            <div className="hb-big">%{pct}</div>
            <div className="hb-sub">{isComplete ? "Tamamlandı" : "Kullanıldı"}</div>
          </div>

          <span className="hb-vline" />

          {/* Sağ segment: badge varsa rozeti, yoksa %'yi ortalamak için boş kalır */}
          <div className="hb-seg hb-seg-badge">
            {badge && (
              <span className="hb-badge">
                <FaCheckCircle /> {badge}
              </span>
            )}
          </div>
        </div>

        {/* İlerleme çubuğu */}
        <div className="hb-progress">
          <div className="hb-track">
            {/* Eşik aşılmadan yeşil gradyan (CSS varsayılanı); aşılınca eşik rengi. */}
            <div
              className="hb-track-fill"
              style={{
                width: `${pct}%`,
                ...(accentColor ? { background: accentColor } : {}),
              }}
            />
            {marks.map((mk) => (
              <span
                key={mk}
                className={`hb-mark ${pct >= mk ? "filled" : ""}`}
                style={{ left: `${mk}%` }}
              >
                <span className="hb-mark-label">{mk}%</span>
                <span className="hb-mark-dot" />
              </span>
            ))}
            <span className="hb-handle" style={{ left: `${pct}%` }} />
          </div>

          {/* Eşik uyarısı (genel havuz mesajı; admin "Eşik Ayarları"ndan) */}
          {alertText && (
            <div className="hb-alert">
              {isTopWarning ? <FaExclamationTriangle /> : <FaBell />}
              <span>{alertText}</span>
            </div>
          )}

          <div className="hb-msg">
            {isComplete ? (
              <FaRegClock className="hb-crown" />
            ) : (
              <FaCrown className="hb-crown" />
            )}{" "}
            {isComplete ? "Puan kazanımı durdu" : "Puan kazanımı devam ediyor"}
          </div>
        </div>
      </div>

      {/* %100: "puan kazanımı durdu" — pozitif çerçeveli tamamlandı bildirimi */}
      {isComplete && (
        <div className="hb-completed">
          <div className="hb-completed-content">
            <div className="hb-completed-title">
              <FaCheckCircle /> Bu ayın kampanya bütçesi tamamlandı — puan
              kazanımı durdu
            </div>
            <div className="hb-completed-msg">{completedText}</div>
            <div className="hb-completed-rozet">
              <FaAward /> Rozet kazanımınız devam ediyor — alımlarınız rozet
              ilerlemenize katkı sağlar.
            </div>
          </div>
          <button
            type="button"
            className="hb-completed-link"
            onClick={() => navigate("/profile")}
          >
            Rozetlerimi Gör →
          </button>
        </div>
      )}
    </Container>
  );
};

export default HomeBudgetStrip;
